import { Component, ComponentRef, ElementRef, HostBinding, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { NavBarGet, NavBarService } from './nav-bar.service';
import { SideNavService } from '../side-nav/side-nav.service';
import { debounceTime, interval, Subscription, Subject, switchMap } from 'rxjs';
import { FormControl } from '@angular/forms';
import { ListboxSelectEvent } from '@app/core/directives/listbox-popup/listbox-popup.directive';
import { Item } from '../listbox/listbox.component';
import { StateService } from '@app/core/services/state.service';
import { LayoutService } from '@app/core/services/layout.service';
import { ModalService } from '@app/core/services/modal.service';
import { ThemeService } from '@app/services/theme.service';
import { INotification, NotificationsService } from '@app/core/services/notifications.service';
import { ToastData, ToastService } from '@app/core/services/toast.service';
import { ToastComponent } from '../toast/toast.component';
import { UtilityService } from '@app/core/services/utility.service';
import { UserStore } from '@app/core/services/user.store';
import { AiChatPanelService } from '../ai-chat/ai-chat-panel.service';

@Component({
    selector: 'tt-nav-bar',
    templateUrl: './nav-bar.component.html',
    styleUrls: ['./nav-bar.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class NavBarComponent implements OnDestroy, OnInit {
    public get?: NavBarGet;

    public searchtext$ = new FormControl<string>('');

    public results: any[] = [];

    private unreadNotificationsSubject = new Subject<void>();

    private notificationSubscription?: Subscription;

    public mobilesearch = false;

    @ViewChild('mobileInputRef')
    public mobileInput?: ElementRef<HTMLInputElement>;

    @HostBinding('class.tt-navbar--danger')
    public get isHeaderColorDanger() {
        return this._isheaderColorDanger;
    }
    private _isheaderColorDanger = false;

    constructor(
        public element: ElementRef<HTMLElement>,
        public navBar: NavBarService,
        public sideNav: SideNavService,
        public state: StateService,
        public layout: LayoutService,
        public modal: ModalService,
        public theme: ThemeService,
        public notifications: NotificationsService,
        public aiChatPanel: AiChatPanelService,
        private toast: ToastService,
        private utility: UtilityService,
        private user: UserStore,
    ) {
        this.loadGet();

        layout.layoutChanged.subscribe({
            next: (info) => {
                this._isheaderColorDanger = info?.windowheadingcolor === 'danger';
            },
        });

        this.searchtext$.valueChanges
            .pipe(
                debounceTime(200),
                switchMap((query) => this.navBar.search(query || '')),
            )
            .subscribe({ next: (items) => (this.results = items) });

        this.unreadNotificationsSubject.pipe(switchMap(async () => (await this.notifications.loadUnreadNotifications()).records)).subscribe({
            next: (value) => {
                if (this.user.showNotificationToasts) {
                    this.addNotificationsToToasts(value);
                }
            },
        });
    }

    public onSelect(event: ListboxSelectEvent<Item>) {
        if (typeof event.item === 'object' && !!event.item.item_state) {
            this.state.go(event.item.item_state ?? '', event.item.item_parms);
        }
    }

    public onGlobalSearchFocus() {
        this.mobileInput?.nativeElement.select();

        if (!this.layout.getMediaQueries().tablet.matches) {
            this.sideNav.open$.next(false);
        }
    }

    public setMobileGlobalSearch() {
        this.mobilesearch = true;

        setTimeout(() => this.mobileInput?.nativeElement?.focus());
    }

    private addNotificationsToToasts(notifications: INotification[]) {
        notifications
            .slice(0, 30)
            .reverse()
            .filter((notification) => notification.closed === '0')
            .map(this.mapNotificationToToastData)
            .forEach((toast) => this.toast.addToast(toast));
    }

    private mapNotificationToToastData = (notification: INotification): ToastData => {
        return {
            id: `notifications.${notification.item_id}`,
            title: notification.item_name ?? '',
            translateTitle: false,
            message: notification.item_name_sub1,
            translateMessage: false,
            type: 'primary',
            hideDelay: null,
            onClick: async ({ event, toast }) => this.handleToastClick(notification, event, toast),
            onClose: () => this.notifications.setAsClosed(notification),
            order: '' + notification.orderby!,
        };
    };

    private async handleToastClick(notification: INotification, event: MouseEvent, toast: ComponentRef<ToastComponent>) {
        await this.notifications.setAsRead(notification);
        this.toast.animateHide(toast, 1000, 'running');

        if (!notification.item_state) return;

        if (this.utility.isModifierKeyPressed(event)) {
            this.state.newTab(notification.item_state, notification.item_parms);
        } else {
            this.state.go(notification.item_state, notification.item_parms);
        }
    }

    private async loadGet() {
        this.get = await this.navBar.get();

        if (!!this.get) {
            this.sideNav.logo$.next(this.get.mainmenu_logo);
            this.sideNav.name$.next(this.get.user_name);
            this.sideNav.company$.next(this.get.company_name);

            this.setInitialSideNavState();

            if (!isNaN(+this.get.notification_interval) && +this.get.notification_interval > 0) {
                this.unreadNotificationsSubject.next();
                this.notificationSubscription = interval(+this.get.notification_interval).subscribe({ next: () => this.unreadNotificationsSubject.next() });
            } else {
                this.notificationSubscription = this.navBar.heading$.pipe(debounceTime(200)).subscribe({ next: () => this.unreadNotificationsSubject.next() });
            }
        }
    }

    private setInitialSideNavState = () => {
        if (!!this.get) {
            if (!this.layout.getMediaQueries().tablet.matches) {
                this.sideNav.setState(this.get.sidebar_open_mobile);
            } else {
                this.sideNav.setState(this.get.sidebar_open_desktop);
            }
        }
    };

    ngOnInit(): void {
        this.layout.getMediaQueries().tablet.addEventListener('change', this.setInitialSideNavState);
    }

    ngOnDestroy(): void {
        this.layout.getMediaQueries().tablet.removeEventListener('change', this.setInitialSideNavState);
        this.notificationSubscription?.unsubscribe();
    }
}
